import { useEffect, useState } from 'react';
import { cleanTag, displayTag, isSeed } from '@engineer-os/chat';
import type { Whois } from '../../lib/chat';
import { metaFor } from '../../lib/chat';
import { personPath } from '../../lib/person';
import { fetchRecord, type PublicRecord } from '../../lib/whois';
import { rankFor } from '../../progress';
import { shortDate } from '../../lib/format';
import { useLocale, useStrings } from '../../state/useLocale';
import { Button, LinkButton, Machine } from '../../ui';
import { OperatorPlate } from '../avatar/Operator';
import { Avatar, VerifiedMark } from './Identity';

/**
 * Who somebody is, opened by clicking their name.
 *
 * Until this existed a person in a room was a name, a tag and four characters
 * of meta squeezed into a message header, and there was nowhere to put the
 * rest of what the room already knew about them. That is the whole of the
 * change: nothing new is asked of the server, nothing new is stored, and
 * nothing new travels. Every line on this card came off the socket that was
 * already open, which is why it can be opened for somebody who left an hour
 * ago as cheaply as for somebody typing right now.
 *
 * ## Why it takes the panel over
 *
 * The same decision the rename form and the report dialog make, for a
 * different reason than theirs. Those take the panel over because they are
 * deliberate stops. This takes it over because it does not fit: the panel's
 * floor is 280px, the figure is shown as itself rather than at sixteen pixels
 * in a list, and a popover holding a plate, a name, a tag, two lines of meta
 * and three actions would cover the conversation it is about. A takeover with
 * one way back is more honest about that than a popover clipped by the
 * message list, and the member list — which is where most of these are opened
 * from — is a popover already. Two stacked popovers is the arrangement the
 * panel's one-at-a-time rule exists to prevent.
 *
 * ## What it will not claim
 *
 * The name is the account's wherever the check beside it says so, and that is
 * the only half of this card the server vouches for. The level and the rank
 * arrived in a `hello` or a `me` frame, which is a browser describing itself;
 * grading runs in the page ([docs/cheating.md](../../../../docs/cheating.md)),
 * so a level is a claim and not a credential. The card says so in a sentence
 * at the bottom rather than implying otherwise by being silent, because this
 * is the first screen in the product that shows one learner a fact about
 * another and the temptation it has to refuse is to look like a record.
 *
 * There is deliberately no count of anything. Levels cleared, stars, time
 * spent — the tab has all of it about *itself* and none of it about anybody
 * else, and the shapes that would fill those rows are the shapes a leaderboard
 * is made of. What is here is what answers the question the click asked: who
 * is this, and are they somebody I can ask.
 *
 * ## The two states, and why the second one is the common one
 *
 * `here` is whether the member list has them. A room keeps a thousand messages
 * and a member list holds whoever is connected this second, so most of the
 * names on screen at any moment belong to people who have closed the tab, and
 * clicking one of those is the ordinary case rather than the corner. Gone, the
 * card is drawn from the message's own snapshot of its sender — taken when it
 * was written, never updated — so it says what it is instead of pretending to
 * be live, and the objective line is left off entirely, because the one thing
 * a stale snapshot should never claim is what somebody is doing right now.
 *
 * ## Reporting is not on this card
 *
 * It looks like it belongs here and it cannot be here: a report is a row with
 * a `message_id` and the message's text copied into it
 * (`worker/migrations/0004_chat.sql`), so that whoever reads the queue can see
 * what was actually said after the room has trimmed it. A report about a
 * person with no message attached would arrive as an accusation with no
 * evidence, which is not a thing the moderation desk can act on. So reporting
 * stays on the message, where there is something to copy. Blocking is here,
 * because blocking genuinely is about a person: it is a list of tags in this
 * one browser, it is never sent, and nobody is told.
 */
export interface ProfileCardProps {
  /** Who, and whether the room has them right now. */
  who: Whois;
  /** True on your own card. Clicking your own name is allowed, not special. */
  own: boolean;
  /** Whether this browser is hiding their messages right now. */
  blocked: boolean;
  /** A listener may read a room and act in it in no way at all. */
  canPost: boolean;
  /**
   * Whether this reader has an account.
   *
   * The record half of the card is for signed-in readers only — the route
   * answers 404 without a session, deliberately — so this decides between
   * asking and saying why not. Asking anyway would work and would cost a
   * request per card to be told no, and would leave a signed-out reader
   * watching a section load into nothing with no explanation.
   */
  signedIn: boolean;
  /**
   * The teams this reader leads, and the way to ask somebody into one.
   *
   * Only the ones they lead: a member cannot invite, the Worker refuses it,
   * and offering the choice would be offering a button that comes back as an
   * error. Empty means there is nothing to ask anybody into yet, which the
   * card says rather than hiding the action — "make a team first" is a useful
   * sentence and a missing button is not.
   */
  leads: Array<{ id: string; name: string }>;
  /** Ask them into one. The answer is the Worker's, already worded. */
  onInvite(teamId: string): Promise<string | null>;
  /** Put `@their name` in the composer. Only offered while they are here. */
  onMention(): void;
  onBlock(on: boolean): void;
  onClose(): void;
}

export function ProfileCard({ who, own, blocked, canPost, signedIn, leads, onInvite, onMention, onBlock, onClose }: ProfileCardProps) {
  const s = useStrings().chat;
  const locale = useLocale().locale;
  const { member, here } = who;

  /**
   * The record behind the tag: absent until it arrives, and `null` once it is
   * known there is not one.
   *
   * Three states rather than two, because "still asking" and "asked, and there
   * is nothing" are different things to draw and collapsing them would flicker
   * the empty sentence in front of every reader on the way to the numbers.
   */
  const [record, setRecord] = useState<PublicRecord | null | undefined>(undefined);
  /**
   * The invitation, in the three states it has.
   *
   * `null` is the ordinary one. `'picking'` is the list of teams, which is
   * shown only when there is more than one to choose between — asking somebody
   * into your only team should be one press, and a menu with one item in it is
   * a menu asking you to confirm that you meant the only thing you could have
   * meant. Anything else is the answer, already worded, on screen until the
   * card closes.
   */
  const [asking, setAsking] = useState<null | 'picking' | string>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Whether there is an account behind this name at all.
   *
   * The route cannot tell an account with no progress from a name that was
   * never an account — both are 404, deliberately, so that probing learns
   * nothing about who exists. The tab can: `verified` rides on every member and
   * every message and is the room saying it vouches for the name. So the check
   * happens here, and the request is not made at all for a name that could
   * never have a record.
   *
   * This is not a corner. Every message sent before a deployment configured
   * sign-in carries a browser-derived tag, and that tag is not in `chat_tags`
   * and never will be — so on a room with any history at all, most of the names
   * a reader can click are these.
   */
  const hasIdentity = cleanTag(member.tag) !== null;
  const isAccount = member.verified === true && hasIdentity;

  useEffect(() => {
    // Two different absences, and the card says which. Asking anyway would
    // spend a request per click to be told 404 by a route that was always
    // going to say 404.
    if (!isAccount || !signedIn) {
      setRecord(null);
      return;
    }
    // The guard is the race, and the race is ordinary here: click a name,
    // click back, click another, and two requests are in flight about two
    // different people. Without this the slower answer wins and the card ends
    // up headed by one person and footed by another.
    let live = true;
    setRecord(undefined);
    void fetchRecord(member.tag).then((r) => {
      if (live) setRecord(r);
    });
    return () => {
      live = false;
    };
  }, [member.tag, signedIn, isAccount]);
  const meta = metaFor(member.level, member.rank);
  // The figure shown as itself gets its plate, which is the ninth rail and
  // belongs to the container rather than to the drawing (avatar/README.md).
  // A face or an account picture has no plate to get: they are not operators,
  // and a frame drawn around one would invent a rail its wearer never picked.
  const seed = member.avatar && isSeed(member.avatar) ? member.avatar : null;
  // Only for somebody the room is holding. A snapshot off a message says what
  // they were doing when they wrote it, and an objective is the one fact on
  // this card short-lived enough that saying it late is saying it wrong.
  const objective = here && member.level !== null && member.objective != null ? member.objective : null;
  // Mentioning resolves a typed name against the member list, so a name that
  // matches nobody in the room addresses nobody (components/chat/mentions.ts).
  // Shown and refused rather than hidden: a control that vanishes teaches
  // nobody why it was there, which is the message menu's rule for the same
  // situation.
  const canMention = canPost && here && !own;

  /**
   * Whether asking them into a team is a press this reader could make.
   *
   * Four conditions and every one of them is about the press coming back as
   * something other than an error: you cannot ask yourself, the route wants a
   * session, it resolves the tag through `chat_tags` so a browser-derived name
   * is a 404, and a team you are only a member of refuses the invitation at
   * the Worker. Each of the last three has a sentence of its own below, where
   * the button would have been — a missing button explains nothing, and this
   * one went missing for the commonest reason there is.
   */
  const canAsk = !own && signedIn && isAccount && leads.length > 0 && asking === null;

  return (
    <div className="chat-who">
      <p className="label">{s.who.title}</p>

      <div className="chat-who-head">
        {/* 48 and not the plate's own 128: `Operator` draws at whole multiples
            of sixteen, 48 is three of them, and the plate's padding is narrowed
            to match in the stylesheet so the whole block is 64 square. A panel
            whose floor is 280px does not have room for a portrait. */}
        {seed ? (
          <OperatorPlate seed={seed} size={48} className="chat-who-op" />
        ) : (
          <span className="chat-who-face">
            <Avatar src={member.avatar} tag={member.tag} size={40} />
          </span>
        )}

        <div className="chat-who-id">
          {/* Two rows, and they are two elements rather than one row that
              wraps. A name here is wide enough to want the whole line, and a
              name that filled the line by being `flex-basis: 100%` would then
              align its text to its *own* direction — parking a Latin name
              against the left edge of a card whose figure is on the right, in
              Arabic. Sized to its content inside a row of its own, it starts
              where the card starts in both languages and still reads in its
              own direction. */}
          <div className="chat-who-title">
            {/* A name is the person's own and may be in either script, so it
                takes its direction from itself and is isolated from what is
                beside it (docs/i18n.md §7). */}
            <span className="chat-who-name" dir="auto">
              {member.name}
            </span>
            {member.verified && <VerifiedMark />}
          </div>
          {/* The tag, the level and the rank were all written by the machine
              and stay the way it wrote them, in both languages. */}
          <div className="chat-who-marks">
            <Machine className="msg-tag">#{displayTag(member.tag)}</Machine>
            {meta && <Machine className="msg-meta">{meta}</Machine>}
          </div>
        </div>
      </div>

      <p className="chat-who-where">
        {/* A state, not an announcement — the same lower-case register the
            footer's status line uses. */}
        <span className={here ? 'chat-who-dot is-here' : 'chat-who-dot'} aria-hidden="true" />
        {here ? s.who.here : s.who.gone}
      </p>

      {objective !== null && (
        /* The wire counts objectives from zero and nobody says "objective 0",
           so the conversion is here, at the one call site that shows one. */
        <p className="chat-who-line">{s.who.objective(objective + 1)}</p>
      )}

      {!here && <p className="note">{s.who.goneNote}</p>}

      {/* Closing the half of the card that came off the socket, rather than
          sitting at the bottom under the half that did not. Two disclaimers in
          a row is worse than one — it reads as a card apologising for itself —
          and these two are about different things: this one is about the level
          and the rank in the header above it, and the one at the foot of the
          record is about the three numbers in the record. Each next to what it
          is about, with a heading between them. */}
      <p className="note">{s.who.what}</p>

      {/* --- What the server knows, and what it was told -------------------
          Two groups under one heading, and the order is the point: the dates
          are the server's own clocks and are true, the three numbers under
          them are that account's claim, and the sentence between them says
          which is which. A card that listed all five together would be a card
          that implied the last three had been checked. */}
      <div className="chat-who-record">
        <p className="label">{s.who.record}</p>
        {!isAccount ? (
          /* First, because it outranks the other two: there is no account, so
             signing in would not help and there is nothing to wait for. */
          <p className="note">{s.who.noAccount}</p>
        ) : !signedIn ? (
          <p className="note">{s.who.signedOut}</p>
        ) : record === undefined ? (
          <p className="note" role="status">
            {s.who.loading}
          </p>
        ) : record === null ? (
          <p className="note">{s.who.noRecord}</p>
        ) : (
          <>
            {/* Dates are formatted by the reader's own locale, and the two
                clocks are the only thing on this card nobody can edit. */}
            <p className="chat-who-line">{s.who.joined(shortDate(new Date(record.joined), locale))}</p>
            <p className="chat-who-line">{s.who.seen(shortDate(new Date(record.seen), locale))}</p>
            {record.record === null ? (
              <p className="note">{s.who.noRecord}</p>
            ) : (
              <>
                <p className="chat-who-line">
                  {/* The rank is named here rather than by the Worker: the
                      ladder is in the bilingual catalogue, so a name sent from
                      the server would arrive in English in an Arabic panel. */}
                  {rankFor(record.record.xp).current.name}
                </p>
                <p className="chat-who-line">{s.who.cleared(record.record.cleared)}</p>
                <p className="chat-who-line">{s.who.stars(record.record.stars)}</p>
                <p className="note">{s.who.claim}</p>
              </>
            )}
          </>
        )}
      </div>

      {/* --- Out of the panel ------------------------------------------------
          The one link on this card that leaves the chat, and the answer to the
          question this card used to raise by existing: a record that can only
          be read inside a room by somebody signed in and standing in it is not
          a record anybody has. `/u/<tag>` is the same person with only the
          server-checked half of them on it, readable by anyone, including on a
          card whose record section above is empty.

          A new tab, always. This panel is mounted over a running virtual
          machine, and a same-tab navigation here would throw away a Linux the
          learner has been working in (App.tsx, "One tree, boot included"). */}
      {isAccount && (
        <p className="chat-who-page">
          <LinkButton variant="quiet" href={personPath(member.tag)} target="_blank" rel="noreferrer" title={s.who.pageWhy}>
            {s.who.page}
          </LinkButton>
        </p>
      )}

      {own ? (
        <p className="note">{s.who.you}</p>
      ) : (
        <div className="chat-who-actions">
          <Button
            type="button"
            variant="solid"
            disabled={!canMention}
            title={!here ? s.who.mentionGone : s.who.mentionWhy}
            onClick={onMention}
          >
            {s.who.mention}
          </Button>
          {/* Browser-local, unsent, and told to nobody — so it is offered to a
              listener as readily as to a member. Reading a room is exactly
              when you might want to stop reading one person in it. */}
          <Button type="button" variant="quiet" disabled={!hasIdentity} title={s.menu.blockWhy} onClick={() => onBlock(!blocked)}>
            {blocked ? s.menu.unblock : s.menu.block}
          </Button>
          {/* Asking them into a team, in the row of actions rather than under
              everything below it.
              It sat at the foot of the card for a while, on the argument that
              it is the only action here that reaches another person and waits
              while mentioning and blocking finish on the press. That is true
              about the act and it was the wrong thing to lay the card out by:
              it put the action people open this card looking for beneath a
              disclaimer, a five-line record and a link, in a panel whose floor
              is 280px, where the honest report from somebody who went looking
              for it was that it does not exist. What is different about it is
              said in the sentence on the button, which is where a difference
              of that kind belongs. */}
          {canAsk && (
            <Button
              type="button"
              variant="quiet"
              disabled={busy}
              title={s.teams.inviteWhy}
              onClick={() => {
                // One team is one press. More than one is a choice, and a
                // choice is a list.
                if (leads.length > 1) {
                  setAsking('picking');
                  return;
                }
                setBusy(true);
                void onInvite(leads[0]!.id).then((said) => {
                  setBusy(false);
                  setAsking(said ?? s.teams.invited);
                });
              }}
            >
              {s.teams.invite}
            </Button>
          )}
        </div>
      )}

      {/* --- What the invitation is not, or what it turned into -------------
          The press is up in the row of actions. Everything that is not a
          press stays here: the list, when there is more than one team to
          choose between; the Worker's answer, once there is one; and the two
          sentences that account for a button the reader cannot see.
          A signed-out reader is still told nothing, and that one is
          deliberate — the record above has already said to sign in, and
          repeating it under a feature they have not met is a second copy of
          one sentence. Every other absence now says which absence it is. */}
      {!own && signedIn && (asking !== null || !isAccount || leads.length === 0) && (
        <div className="chat-who-team">
          {asking === 'picking' ? (
            /* Tested before the answer below it, and that order is the whole
               of a bug this once had: `'picking'` is a string like any other
               answer, so a `typeof` check reached it first and printed the
               word itself where the teams should have been — which is what a
               lead of two teams saw every time they pressed Invite. */
            <>
              <p className="label">{s.teams.inviteTo}</p>
              <div className="chat-who-teamlist">
                {leads.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="chat-room-tag"
                    disabled={busy}
                    onClick={() => {
                      setBusy(true);
                      void onInvite(t.id).then((said) => {
                        setBusy(false);
                        setAsking(said ?? s.teams.invited);
                      });
                    }}
                  >
                    <span dir="auto">{t.name}</span>
                  </button>
                ))}
              </div>
            </>
          ) : typeof asking === 'string' ? (
            /* The answer, and no way back to the button. Asking twice is what
               `already asked` refuses, and offering the press again would be
               offering something that comes back as an error. */
            <p className="note" role="status">
              {asking}
            </p>
          ) : !isAccount ? (
            /* An invitation is resolved through `chat_tags`, so a
               browser-derived tag comes back 404 from the Worker. This is the
               commonest state on this card and it used to be the silent one:
               the block was gated on `isAccount` and simply was not drawn. */
            <p className="note">{s.teams.inviteNoAccount}</p>
          ) : (
            /* Nothing to ask anybody into. By elimination: the guard above
               admits this branch only when there is no team led. */
            <p className="note">{s.teams.inviteNoTeam}</p>
          )}
        </div>
      )}

      <div className="chat-who-back">
        <Button type="button" variant="quiet" onClick={onClose}>
          {s.who.back}
        </Button>
      </div>
    </div>
  );
}

export default ProfileCard;
